/**
 * The auth client's reset after a local wipe, bound to the session that
 * ended (Codex on #1136). auth-js's local signOut reads the stored session,
 * revokes it on the server and removes every auth key. The wipe's own checks
 * run under the session store lock, but this reset comes after them: a
 * sign-in that completes in between would lose its new session, on the
 * phone and on the server, to a reset meant for the old one.
 *
 * While a reset is bound, the native auth adapter hands it no session but
 * the ended one: reading another throws, so the reset stops before it
 * revokes or removes anything, and the new sign-in's own SIGNED_IN stands.
 * A removal of an auth key whose value is not the ended session's is
 * skipped, so a sign-in begun since keeps its verifier.
 *
 * No imports: the auth adapter loads before most modules.
 */
export interface AuthResetBinding {
  /** Whether this stored value is the ended session's own, or nothing. */
  isEnded(key: string, value: string | null): boolean;
}

let binding: AuthResetBinding | null = null;

export function currentAuthReset(): AuthResetBinding | null {
  return binding;
}

export async function bindAuthReset<T>(reset: AuthResetBinding, task: () => Promise<T>): Promise<T> {
  binding = reset;
  try {
    return await task();
  } finally {
    if (binding === reset) binding = null;
  }
}

export class NewerSessionError extends Error {
  constructor() {
    super('A newer session is stored: the reset of the ended one stops.');
    this.name = 'NewerSessionError';
  }
}
